/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { Department, ProcedureSummary } from "../shared/types";
import { DepartmentTabs } from "../shared/DepartmentTabs";
import { localDate } from "../shared/dates";
import { ProcedureCard } from "./ProcedureCard";
import { ProcedureEditor } from "./ProcedureEditor";
import { ProcedureRunner } from "./ProcedureRunner";
import { HistoryModal } from "./HistoryModal";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

// Standard operating procedures: checklists and logs, scheduled by weekday.
// See docs/plans/workday/readme.md.
export const WorkDayProceduresPage = defineComponent({
  name: "WorkDayProceduresPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();

    const loading = ref(true);
    const list = ref<ProcedureSummary[]>([]);
    const department = ref<Department | null>(null);
    const editing = ref<{ id: number | null; schedule: boolean } | null>(null);
    const running = ref<number | null>(null);
    const history = ref<ProcedureSummary | null>(null);

    const load = async () => {
      try {
        list.value =
          (await trpc.workday.procedures.list.query()) as ProcedureSummary[];
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };
    onMounted(load);

    const shown = computed(() =>
      list.value.filter(
        (p) => !department.value || p.department === department.value,
      ),
    );
    const used = computed(() => [
      ...new Set(list.value.map((p) => p.department)),
    ]);

    const remove = async (p: ProcedureSummary) => {
      if (
        !confirm(
          t("workday.procedures.delete_confirm").replace("{name}", p.name),
        )
      )
        return;
      try {
        await trpc.workday.procedures.delete.mutate({ id: p.id });
        await load();
      } catch {
        toast.showToast(t("workday.common.save_failed"), { type: "error" });
      }
    };

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("workday.nav.procedures")}
          subtitle={t("workday.procedures.subtitle")}
        >
          <button
            class="btn btn-primary btn-sm"
            onClick={() => (editing.value = { id: null, schedule: false })}
          >
            <span class="material-symbols-outlined text-base">add</span>
            {t("workday.procedures.new")}
          </button>
        </SubpageHeader>
        <div class="flex-1 space-y-6 overflow-auto p-5">
          {list.value.length > 0 && (
            <DepartmentTabs
              value={department.value}
              used={used.value}
              onChange={(d: Department | null) => (department.value = d)}
            />
          )}

          {loading.value ? (
            <div class="flex justify-center p-10">
              <Loading />
            </div>
          ) : list.value.length === 0 ? (
            <div class="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-base-300 bg-base-100 px-6 py-16 text-center">
              <span class="material-symbols-outlined text-5xl text-primary">
                fact_check
              </span>
              <h2 class="text-lg font-semibold">
                {t("workday.procedures.empty_title")}
              </h2>
              <p class="max-w-md text-sm text-base-content/60">
                {t("workday.procedures.empty_help")}
              </p>
              <button
                class="btn btn-primary btn-sm"
                onClick={() => (editing.value = { id: null, schedule: false })}
              >
                {t("workday.procedures.new")}
              </button>
            </div>
          ) : (
            <div class="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {shown.value.map((p) => (
                <ProcedureCard
                  key={p.id}
                  procedure={p}
                  onRun={() => (running.value = p.id)}
                  onEdit={(schedule: boolean) =>
                    (editing.value = { id: p.id, schedule })
                  }
                  onHistory={() => (history.value = p)}
                  onDelete={() => remove(p)}
                />
              ))}
            </div>
          )}
        </div>

        {editing.value && (
          <ProcedureEditor
            procedureId={editing.value.id}
            focusSchedule={editing.value.schedule}
            onClose={async (changed: boolean) => {
              editing.value = null;
              if (changed) await load();
            }}
          />
        )}
        {running.value != null && (
          <ProcedureRunner
            procedureId={running.value}
            date={localDate()}
            onClose={async (changed: boolean) => {
              running.value = null;
              if (changed) await load();
            }}
          />
        )}
        {history.value && (
          <HistoryModal
            procedureId={history.value.id}
            name={history.value.name}
            onClose={() => (history.value = null)}
          />
        )}
      </div>
    );
  },
});
